import React, { useState, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Button } from '@/components/Buttons';
import { rejectBuyerOffer } from '@/services/buyer-dashboard';
import { ChangeRequestProgressInfoOffer } from '@/types';
import { ValidatingTextArea, ValidationScope, ValidationSummary } from '@/components/forms/validation';
import { useValidationManager } from '@/utils/hooks';

interface OrderRejectDialogProps {
    show: boolean;
    residentialUnitId: number;
    offer: ChangeRequestProgressInfoOffer;
    onClose(): void;
    onReject(reason: string): void;
}

const BuyerChangeRequestOfferRejectDialog: React.FC<OrderRejectDialogProps> = ({
    show,
    onClose,
    onReject,
    residentialUnitId,
    offer,
}) => {
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const focusElementRef = useRef<HTMLTextAreaElement>(null);
    const validationManager = useValidationManager();

    const handleOpen = () => {
        setReason('');

        if (focusElementRef.current) {
            focusElementRef.current.focus();
        }
    };

    const handleRejectClick = async () => {
        if (validationManager.validate().length > 0) {
            return;
        }

        setSaving(true);
        try {
            await rejectBuyerOffer(residentialUnitId, offer.id, reason);

            triggerHandler(onReject, reason);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpened={handleOpen}>
            <ModalHeader>
                <Trans>Reject offer</Trans>
            </ModalHeader>
            <ModalBody>
                <ValidationScope validationManager={validationManager}>
                    <div className="form-group">
                        <label>
                            <Trans>Reason</Trans>
                        </label>
                        <ValidatingTextArea
                            ref={focusElementRef}
                            value={reason}
                            className="form-control"
                            fieldName={t`Reason`}
                            placeholder={t`Specify why you are rejecting this offer.`}
                            onChange={(e) => setReason(e.target.value)}
                            minRows={4}
                            maxRows={8}
                            autoResize
                            required
                        />
                    </div>
                    <ValidationSummary />
                </ValidationScope>
            </ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button color="danger" onClick={handleRejectClick} loading={saving}>
                    <Trans>Reject</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default BuyerChangeRequestOfferRejectDialog;
